import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, HostListener } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { concat, Subscription, of, Observable } from 'rxjs';
import { map, tap, concatMap, switchMap, finalize } from 'rxjs/operators';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Title } from "@angular/platform-browser";

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { DocumentStackService } from 'src/app/services/document-stack.service';
import { SecurityGroups } from 'src/app/shared/securitygroups';
import { Document, DocItems, DocumentExport, ImportItemFields } from 'src/app/shared/document';
import { DocTypes } from 'src/app/shared/doc-list-entry';
import { Currency } from 'src/app/shared/currency';
import { PerListEntry } from 'src/app/shared/per-list-entry';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { ProductApiService } from 'src/app/services/product-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';

@Component({
  selector: 'app-edit-document-po',
  templateUrl: './edit-document-po.component.html',
  styleUrls: ['./edit-document-po.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Document editor for DOCTYPE 10 — purchase order.
 *
 * `_docType` is hardcoded to `10` (not read from route params) because this route is
 * only ever reached for purchase orders.
 *
 * `importParamsMap` includes `U4ETKA` (invoice/cost price) and `DOCITEMTEXT1` (expected
 * delivery date, type `'dat'`), distinguishing this editor's import layout from the
 * standard invoice importer.
 *
 * Otherwise follows the same `concatMap` load chain (`getDocumentById` →
 * `getDocumentNameById` → `getDocTypeById`), manual subscription management, and
 * section-visibility helpers as the sibling receipt/spec editors.
 */
export class EditDocumentPoComponent implements OnInit, OnDestroy, OnChanges, ComponentCanDeactivate {

  globalSettinsData: GlobalSettingsData;

  /*private*/ _docId: number = 0;
  /*private*/ _docType: number = 1;

  DOCDATE: Date;
  DOCEXPDATE: Date;
  DOCVALDATE: Date;

  private _initialQuery: Subscription;
  private _productInfoQuery: Subscription;

  private _deleteDocQuery: Subscription;
  private _childDocQuery: Subscription;

  file: any[];

  document: Document;
  documentCopy: Document;

  docTypeInfo: DocTypes;

  displayDC: boolean;

  displayND: boolean;

  displayMD: boolean;

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;

  documentSecurityGroup: SecurityGroups;

  showTabRowInfo: boolean = false;
  showTabDetails: boolean = true;
  showTabSeller: boolean = true;
  showTabCustomer: boolean = true;
  showTabConditions: boolean = true;
  showTabTable: boolean = true;
  showTabTransaction: boolean = false;
  showTabSaleResult: boolean = false;

  movementIsReady: boolean = true;

  result: any[];

  resultIsReady: boolean = false;

  private _documentQuery: Subscription;
  private _documentSaveQuery: Subscription;
  private _documentPostQuery: Subscription;

  docHeaderStatement: string = '';
  docFooterStatement: string = '';

  documentSpecific: DocumentExport;

  toolbarOptions: any = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],

    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
  ];

  modules: any = {
    toolbar: this.toolbarOptions
  };

  /** Returns `true` when all header properties match `documentCopy`; skips computed line-item total fields. */
  public get noChangesThere(): boolean {
    let equal: boolean = true;
    if (this.document && this.documentCopy) {
      for (var props in this.document) {
        //console.log('*** EditDocumentPoComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])

        // Filter out document summury properties (by fact they do not modify document content)

        switch (props) {
          case 'items':
          case 'DOCITEMS':
          case 'DOCTOTALITEMS':
          case 'DOCSUMU4ETKA':
          case 'DOCVSUM':
          case 'DOCSUMWOVAT':
          case 'DOCVAT':
          case 'DOCSUM':
          case 'DOCSUMWEIGHT':
          case 'DOCNAMESTR':
          case 'DOCSUMPROFIT':
            break;

          default:
            equal = (this.document[props] == this.documentCopy[props]);
            break;
        }
        if (!equal) break;
      }
      //console.log('*** EditDocumentPoComponent in noChangesThere: ' + equal)
    }
    return equal;
  }

  /** Returns `true` when `document` is not yet loaded. */
  public get formInvalid(): boolean {
    return (
      !this.document
    );
  }

  /** Returns `true` when a document is loaded and has no unsaved changes (enables create-child action). */
  public get canCreate(): boolean { return (this._docId) && (this.document) && (this.noChangesThere); }

  /** Returns `true` when a document is loaded (enables delete action). */
  public get canDelete(): boolean { return (this._docId) && (this.document && true); }

  exportDS: boolean;

  displayImportDlg: boolean = false;

  importParamsMap: ImportItemFields[] = [
    { fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'U4ETKA', fieldLabel: 'Цена инвойса', fieldIsMandatory: true, fieldType: 'num' },
//    { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Дата поставки', fieldIsMandatory: false, fieldType: 'dat' },
    { fieldName: 'DOCITEMPRICE', fieldLabel: 'Входная цена', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Входная цена $', fieldIsMandatory: false, fieldType: 'num' },
//    { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
  ];

  editing: boolean = false;

  constructor(
    private docApi: DocumentApiService,
    //private restapi: RestApiService,
    //private prodApi: ProductApiService,
    //private cliApi: ClientApiService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private globalsettingsservice: GetglobalsettingsService,
    private documentStack: DocumentStackService,
    private titleService: Title
  ) {

  }

  /** Guards route deactivation and browser unload; blocks navigation when there are unsaved changes or an active inline edit. */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return (this.noChangesThere && !this.editing);
  }

  /** Unsubscribes from all active subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentPoComponent in ngOnDestroy')

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
    if (this._documentQuery) { this._documentQuery.unsubscribe(); }
    if (this._documentSaveQuery) { this._documentSaveQuery.unsubscribe(); }
    if (this._documentPostQuery) { this._documentPostQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
    if (this._deleteDocQuery) { this._deleteDocQuery.unsubscribe(); }
    if (this._childDocQuery) { this._childDocQuery.unsubscribe(); }
  }

  /** Logs simple-changes events (Angular lifecycle hook; no business logic). */
  ngOnChanges(changes: SimpleChanges): void {
    console.log('*** EditDocumentPoComponent ngOnChanges: ===> ' + JSON.stringify(changes));
  }

  /**
   * Loads the full document via a sequential `concatMap` chain:
   * `getDocumentById` → `getDocumentNameById` → `getDocTypeById`, with a `finalize`
   * snapshot of `documentCopy`. New-document mode reads from `DocumentStackService` at index 0.
   */
  loadDocumentContent() {
    console.log('*** EditDocumentPoComponent loadDocumentContent: ===> Started');
    if (this._docId) {
      console.log('*** EditDocumentPoComponent loadDocumentContent docid: ' + this._docId);

      this._documentQuery =
        this.docApi.getDocumentById(this._docId).pipe(

          concatMap(doc => {

            this.document = doc[0];
            this.DOCDATE = new Date(this.document.DOCDATE);
            this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
            this.DOCVALDATE = new Date(this.document.DOCVALDATE);
            this._docType = this.document.DOCTYPE;

            console.log('*** EditDocumentPoComponent in map loadDocumentContent: ===> ' + this.DOCDATE);

            return of(this.document);
          }),

          concatMap(doc => this.docApi.getDocumentNameById(doc.DOCID).pipe(map(data => {
            let docName: any = data[0];

            console.log('*** EditDocumentPoComponent loadDocumentName: ===> doc 0: ' + JSON.stringify(docName));

            if (docName) {
              this.document.DOCNAMESTR = docName.DOCNAMESTR;
              this.titleService.setTitle(this.document.DOCNAMESTR);
            }
            return doc;
          }))),

          concatMap(doc => this.docApi.getDocTypeById(doc.DOCTYPE).pipe(map(data => {
            console.log('*** EditDocumentPoComponent in get DOCTYPE: ===> ' + JSON.stringify(data));

            this.docTypeInfo = data[0];

            this.document.HeaderStatement = this.docTypeInfo.DOCTEXTBEFORE;
            this.document.FooterStatement = this.docTypeInfo.DOCTEXTAFTER;
          }))),

          finalize(() => { this.documentCopy = { ... this.document }; })

        ).subscribe();
    } else {
      /*
       * (this._prodId == 0) New Document Mode
       *
       * get Document from Document Stack
       */
      let newIndex = 0;

      /*
       * Request new Index from query string param.
       */
      this.document = this.documentStack.getDocument(newIndex);
      if (this.document) {

        this.DOCDATE = new Date(this.document.DOCDATE);
        this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
        this.DOCVALDATE = new Date(this.document.DOCVALDATE);
      }
    }
    console.log('*** EditDocumentPoComponent loadDocumentContent: ===> Ended');
  }

  /** Reads global settings, hardcodes `_docType` to 10, and subscribes to `paramMap` to reload the document on route changes. */
  ngOnInit(): void {
    console.log('*** EditDocumentPoComponent in ngOnInit')
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this._initialQuery =
      this.route.paramMap
        .subscribe((params: ParamMap) => {
          this._docId = +params.get('id');
          this._docType = 10 /*+params.get('type')*/;
          console.log('*** EditDocumentPoComponent in param subscribe: (' + this._docType + ') (' + this._docId + ') ' + JSON.stringify(params));

          this.loadDocumentContent();
        });
  }

  /** Generic button-click stub (logging only). */
  buttonClick(event) {
    console.log('*** EditDocumentPoComponent in buttonClick: ' + JSON.stringify(event));
  }

  /** Updates `DOCDATE` and auto-computes `DOCVALDATE` / `DOCEXPDATE` from the doc-type's default terms. */
  onDateSelect(event) {
    console.log('*** EditDocumentPoComponent in onDateSelect: ' + JSON.stringify(event));

    let dddd = new Date(event);
    this.document.DOCDATE = dddd;

    this.document.DOCVALDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTVALTERM || 0))
    this.document.DOCEXPDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTEXPTERM || 0))

    this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
    this.DOCVALDATE = new Date(this.document.DOCVALDATE);
  }

  /** Sets `DOCEXPDATE` on the document when the expiry date picker fires. */
  onDateExpSelect(event) {
    console.log('*** EditDocumentPoComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCEXPDATE = event;
  }

  /** Sets `DOCVALDATE` on the document when the validity date picker fires. */
  onDateValSelect(event) {
    console.log('*** EditDocumentPoComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCVALDATE = event;
  }

  /** Updates `document.DOCCURRID` when the currency dropdown fires a raw ID change. */
  onDocCurrencyChange(event) {
    console.log('*** EditDocumentPoComponent in onDocCurrencyChange: ' + JSON.stringify(event));

    this.document.DOCCURRID = event;
  }

  /** Updates `DOCCURRID` and `DOCRATEVAL` from the selected currency object. */
  onCurrencySelection(event) {
    if (this.document.DOCCURRID != event.CURID) {
      this.document.DOCCURRID = event.CURID;
    }
    this.document.DOCRATEVAL = event.CURRATE;
  }

  /** Handles selection in the recent-documents panel (logging stub). */
  onRecentRowSelect(selectedId) {
    console.log('*** EditDocumentPoComponent in onRecentRowSelect: ' + selectedId);
  }

  /** Logs which accordion tab was opened (stub). */
  onTabOpen(event) { console.log('*** EditDocumentPoComponent in onTabOpen: ' + event.index); }

  /** Sets `document.PRIMDOCID` to the selected parent's `DOCID`, or `null` if cleared. */
  parentDocumentSelected(event) {
    console.log('*** EditDocumentPoComponent in parentDocumentSelected: ' + JSON.stringify(event));
    this.document.PRIMDOCID = (event) ? event.DOCID : null;
  }

  /** Applies the chosen security group to `documentSecurityGroup` and writes its `SECGROUPID` to `document.SGMGROUPID`. */
  onSelectedSecurityGroup(sg: SecurityGroups) {
    console.log('*** EditDocumentPoComponent in onSelectedSecurityGroup: ' + JSON.stringify(sg));
    this.documentSecurityGroup = sg;
    this.document.SGMGROUPID = sg.SECGROUPID;
  }

  /** Saves the document: PUT (update) when `_docId > 0`, POST (create) when `_docId === 0`, then navigates to the new document route on success. */
  onConfirm(e: number) {
    console.log('*** EditDocumentPoComponent onConfirm: ' + e)

    switch (e) {
      default:
        console.log('*** EditDocumentPoComponent onConfirm: (Main data) ');
        environment.production || console.log('*** EditDocumentPoComponent buttonSaveAllClick: document ' + JSON.stringify(this.document));
        environment.production || console.log('*** EditDocumentPoComponent buttonSaveAllClick: documentCopy ' + JSON.stringify(this.documentCopy));

        if (this._docId) {
          this._documentSaveQuery =
            this.docApi.putDocumentById(this.document).pipe(
              tap(data => (environment.production || console.log('*** EditDocumentPoComponent onConfirm: *** on data map *** ' + data))),

              finalize(() => {
                this.documentCopy = { ... this.document };
                this.document = { ... this.document };        // Trigger the changes in child components
                environment.production || console.log('*** EditDocumentPoComponent onConfirm: *** PUT *** ');
              })
            )
              .subscribe();
        } else {
          this._documentPostQuery =
            this.docApi.postDocumentById(this.document).pipe(
              map(data => {
                environment.production || console.log('*** EditDocumentPoComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data));
                this._docId = data['DOCID'];
              }),
              finalize(() => {
                this.router.navigate(['/edit-document', this._docId]);
                environment.production || console.log('*** EditDocumentPoComponent onConfirm: *** POST *** ');
              })
            )
              .subscribe();
        }
        break;
    }
  }

  /** Shows only the conditions tab, hiding all other sections. */
  showConditions(event) {
    console.log('*** EditDocumentPoComponent in showConditions: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = true;
    this.showTabTable = false;
    this.showTabTransaction = false;
    this.showTabSaleResult = false;
  }

  /** Shows only the content (items) tab, hiding all other sections. */
  showContent(event) {
    console.log('*** EditDocumentPoComponent in showContent: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = false;
    this.showTabTable = true;
    this.showTabTransaction = false;
    this.showTabSaleResult = false;
  }

  /** Shows only the transactions tab, hiding all other sections. */
  showTransactions(event) {
    console.log('*** EditDocumentPoComponent in showTransactions: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = false;
    this.showTabTable = false;
    this.showTabTransaction = true;
    this.showTabSaleResult = false;
  }

  /** Handles new-document creation events from the child toolbar (stub — overlay logic not yet implemented). */
  onNewDocument(event) {
    console.log('*** EditDocumentPoComponent onNewDocument: ' + JSON.stringify(event))
  }

  /** Triggers a shallow document clone to propagate refilled content to child components. */
  onRefillDocument(event) {
    console.log('*** EditDocumentPoComponent onRefillDocument: ' + JSON.stringify(event))
    if (event) {
      this.document = { ...this.document }; // Trigger reload
      environment.production || console.table(this.document);
      environment.production || console.table(this.document.items);
    }
  }

  /** Builds the export label map and opens the export overlay immediately (no item-highlight guard for purchase orders). */
  onExportDocument(event) {
    console.log('*** EditDocumentPoComponent onRefillDocument: ' + JSON.stringify(event))
    environment.production || console.table(this.document);
    environment.production || console.table(this.document.items);

    this.documentSpecific = {
      /*
      Owner: {
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Контактний телефон',
      },

      Header: {
        DateLabel: 'Дата',
        DocumentNameLabel: 'Назва',
        DocumentNumberLabel: 'Номер',
        ValidToLabel: 'Дійсний до',
        ExpiredOnLabel: 'Дата виконання',

        CurrencyNameLabel: 'Валюта документа',
        CurrencyRateLabel: 'Курс валюти',
        MainCurrencyNameLabel: 'Валюта документа',

      },

      Seller: {
        CompanyNameLabel: 'ПРОДАВЕЦЬ',
        CompanyIdLabel: 'Код ЄДРПОУ',
        VATIDLabel: 'Код платника ПДВ',
        VATIDDocLabel: 'Свідоцтво ПДВ',
        AccountLabel: 'Рахунок',
        BankLabel: 'Банк',
        AddressLabel: 'Адреса',
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Телефон',
      },

      Buyer: {
        CompanyNameLabel: 'ПОКУПЕЦЬ',
        CompanyIdLabel: 'Код ЄДРПОУ',
        VATIDLabel: 'Код платника ПДВ',
        VATIDDocLabel: 'Свідоцтво ПДВ',
        AccountLabel: 'Рахунок',
        BankLabel: 'Банк',
        AddressLabel: 'Адреса',
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Телефон',
      },

      ItemsLabel: {
        ItemCodeLabel: 'Код товару',
        ItemNameLabel: 'Найменування товару',
        ItemTariffCodeLabel: 'Код УКТЗЕД',
        ItemNoteLabel: 'Примітки',
        ItemPriceLabel: 'Ціна з ПДВ',
        ItemVPriceLabel: 'Ціна в валюті',
        ItemSumWoVATLabel: 'Сума без ПДВ',
        ItemPriceWoVATLabel: 'Ціна без ПДВ',
        ItemRetailPriceLabel: 'Роздріб',
        ItemRetailVPriceLabel: 'Роздріб в валюті',
        ItemDiscountLabel: 'Знижка',
        ItemQntyLabel: 'Кількість',
        ItemSumLabel: 'Сума з ПДВ',
        ItemVSumLabel: 'Сума в валюті',
        ItemVATLabel: 'ПДВ',

        ItemVendorPriceLabel: 'ItemVendorPriceLabel',
        ItemInvoicePriceLabel: 'ItemInvoicePriceLabel',
        ItemInvoiceSumLabel: 'ItemInvoiceSumLabel',

        ItemIncomePriceLabel: 'ItemIncomePriceLabel',
        ItemIncomeSumLabel: 'ItemIncomeSumLabel',

        ItemProdWeightLabel: 'ItemProdWeightLabel',
        ItemProdWeightSumLabel: 'ItemProdWeightSumLabel',

      },

      Conditions: {
        DeliveryConditionsLabel: 'Умови постачання',
        DeliveryTermsLabel: 'Очікуванний термін виконаня (днів)',
        DocumentNoteLabel: 'Примітки документа',
      },

      Summary: {
        TotalSumLabel: 'Загалом, з ПДВ',
        TotalVSumLabel: 'Загалом, з ПДВ, в валюті',
        TotalSumWithOutVATLabel: 'Сума без ПДВ',
        TotalSumVATLabel: 'Сума ПДВ',
        TotalItemsLabel: 'Усього найменувань',
        TotalQuantityLabel: 'Загальна кількість',
        TotalByWordsLabel: 'Усього прописом',
      }
    */};

    this.exportDS = true;
  }

  /** Executes confirmed document deletion and navigates to `/documents` on success. */
  onDeleteDocumentConfirmation(event) {
    console.log('*** EditDocumentPoComponent onDeleteDocumentConfirmation: ' + JSON.stringify(event))

    this._deleteDocQuery =
      this.docApi.deleteDocumentById(this.document.DOCID).subscribe(() => {
          console.log('*** EditDocumentPoComponent onDeleteDocumentConfirmation: DONE!');
          this.router.navigate(['/documents']);
        });
  }

  /** Guards deletion by checking `getChildDocList`; opens confirmation if no children, or shows an error toast if children exist. */
  deleteDocumentClick(event) {
    console.log('*** EditDocumentPoComponent deleteDocumentClick: ' + JSON.stringify(event))

    this._childDocQuery =
      this.docApi.getChildDocList(this._docId).pipe(map(doc => {
        if (doc.length == 0) {
          this.confirmDeleteDocumentText = 'DELETE ' + this.document.DOCNUMBER;
          this.confirmDeleteDocumentOn = true;
        } else {
          this.messageService.add({ severity: 'error', summary: 'Этот документ имеет подчиненные - удалить невозможно.' });
        }
      })).subscribe();
  }

  /** Replaces `document.items` with the imported rows and triggers a shallow document clone to notify child components. */
  onImportDone(event) {
    console.log('*** EditDocumentPoComponent onImportDone: ');

    environment.production || console.table(event);

    this.document.items = event;
    this.document = { ...this.document };

  }

}

